import { useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import {
  homePathFor,
  useHomePersona,
  type HomePersona,
} from "@shared-core/domain/homePersona";
import {
  isScopeOverrideRole,
  useScopeOverrideValue,
} from "@shared-core/domain/scopeOverride";
import { useDebouncedValue } from "@shared-core/domain/useDebouncedValue";
import type {
  CsrHomeDashboard,
  WorkUnitSummaryItem,
} from "@shared-core/data/api-types";
import type { InFlightBoardSort } from "@shared-core/data/adapter";
import { paths } from "@shared-core/routing/routes";
import { useAuth } from "../app/auth/hooks";
import { HomePersonaToggle } from "../app/components/patterns/HomePersonaToggle";
import {
  CommandCentreStatStrip,
  type CommandCentreStatTileKey,
} from "../app/components/domain/csr-command-centre/CommandCentreStatStrip";
import { CommandCentreReadinessStrip } from "../app/components/domain/csr-command-centre/CommandCentreReadinessStrip";
import { CommandCentreRail } from "../app/components/domain/csr-command-centre/CommandCentreRail";
import { WipFocusedBoard } from "../app/components/domain/csr-command-centre/PortfolioBoard";

const _DEFAULT_SORT: InFlightBoardSort = "worst_first";

/**
 * Alternative CSR home layout — under side-by-side evaluation against the
 * approved "Portfolio board" design (`CsrHome.tsx`), reached only via its
 * own URL (`/home/csr/alt`, not linked from nav or the persona toggle).
 *
 * Differs from `CsrHome` in two ways:
 *
 * 1. The main column drops the deadline/readiness board lanes (Not
 *    production ready / Due today / Due soon / At risk / Late) and the
 *    stuck-orders section entirely. In their place: one combined,
 *    worst-first list of every in-flight job (`dashboard.in_flight_board`
 *    — csr-dashboard-alt-screen), rendered fully expanded with no
 *    in-place preview cap (see `WipFocusedBoard`) — every card the real
 *    `/home/csr` page would otherwise split across five lanes, in one
 *    place, red/amber/green worst first. "Recently completed" and
 *    "Finished — awaiting close" still follow it, unchanged, because
 *    they're independent of the lanes being dropped. The readiness strip
 *    stays — it wasn't one of the removed lanes.
 * 2. The four stat tiles navigate straight to the jobs page's work view
 *    with the matching filter applied, instead of scrolling to a lane (there
 *    is no longer a lane for three of the four to scroll to).
 *
 * Everything else (header, scope toggle, no-portfolio banner, right rail)
 * is identical to `CsrHome` — duplicated here rather than shared, which is
 * an acceptable amount of overlap for an evaluation page comparing two full
 * layouts side by side.
 */
export function CsrHomeAlt() {
  const dataAdapter = useDataAdapter();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { setPersona } = useHomePersona({ role: undefined });
  const { showAll, setShowAll } = useScopeOverrideValue({ role: user?.role });
  const canToggleScope = isScopeOverrideRole(user?.role);
  const [dashboard, setDashboard] = useState<CsrHomeDashboard | null>(null);
  const [loading, setLoading] = useState(true);

  // WIP search/sort (csr-dashboard-alt-screen-0fef93). Persisted in the URL
  // (`?q=`/`?sort=`) so it survives a refresh; only non-default values are
  // written, mirroring WorkPortfolioPage's URL-sync pattern.
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(() => searchParams.get("q") ?? "");
  const [sort, setSort] = useState<InFlightBoardSort>(
    () =>
      (searchParams.get("sort") as InFlightBoardSort | null) ?? _DEFAULT_SORT,
  );
  const debouncedQuery = useDebouncedValue(query, 300);
  const isSearchActive = debouncedQuery !== "" || sort !== _DEFAULT_SORT;
  const [boardResult, setBoardResult] = useState<{
    items: WorkUnitSummaryItem[];
    total: number;
  } | null>(null);
  const [boardLoading, setBoardLoading] = useState(false);

  const switchPersona = (next: HomePersona) => {
    setPersona(next);
    navigate(homePathFor(next));
  };

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const result = await dataAdapter.getCsrHome();
        if (!cancelled) setDashboard(result);
      } catch (err) {
        console.error("Error loading CSR dashboard:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [dataAdapter]);

  // Mirror query/sort into the URL — replace, not push, so search-as-you-
  // type doesn't spam browser history.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (debouncedQuery) next.set("q", debouncedQuery);
    else next.delete("q");
    if (sort !== _DEFAULT_SORT) next.set("sort", sort);
    else next.delete("sort");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reads searchParams only to preserve any other params; including it would loop
  }, [debouncedQuery, sort]);

  // Only hit the dedicated search endpoint when search/sort is actually
  // active — the default view already has its worklist from
  // `dataAdapter.getCsrHome()` above, so this avoids a redundant fetch on
  // first load and lets clearing the search snap back instantly.
  useEffect(() => {
    if (!isSearchActive) {
      setBoardResult(null);
      return;
    }
    let cancelled = false;
    setBoardLoading(true);
    dataAdapter
      .getInFlightBoard({ q: debouncedQuery || undefined, sort })
      .then((result) => {
        if (!cancelled) setBoardResult(result);
      })
      .catch((err) => {
        console.error("Error searching in-flight board:", err);
      })
      .finally(() => {
        if (!cancelled) setBoardLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dataAdapter, isSearchActive, debouncedQuery, sort]);

  if (loading || !dashboard) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-muted-foreground">Loading your dashboard…</div>
      </div>
    );
  }

  const inFlightBoard = boardResult?.items ?? dashboard.in_flight_board;
  const inFlightBoardTotal =
    boardResult?.total ?? dashboard.in_flight_board_total;

  // Tile -> jobs-page filter. "Mine" mirrors the dashboard's own scope
  // (the CSR's portfolio + bookings) — omitted when "Show all customers"
  // is on, since sending both would silently re-narrow a list the toggle
  // just asked to widen. Naming any filter here (due_window, readiness, or
  // mine on its own) already suppresses the jobs page's default
  // alerts-only narrowing (see `computeDefaultAlertsOnly`), so these counts
  // are not further narrowed by that default.
  //
  // Known definitional gap: these counts are CSR-home's own tallies
  // (`summary_counts`), which exclude jobs with a suspect (year-wrong)
  // needed-by date from "overdue" (see `boardFlag` / `needed_by_year_suspect`
  // in PortfolioBoard.tsx). The jobs-page `due_window=overdue` filter has no
  // equivalent exclusion, so a job with a suspect date that looks overdue
  // can appear in the filtered list without having counted towards the
  // tile. This is a pre-existing gap between the two surfaces, not
  // introduced here — not papered over.
  function selectStatTile(tile: CommandCentreStatTileKey) {
    const mine = !showAll;
    if (tile === "not_production_ready") {
      navigate(paths.jobs({ view: "work", readiness: tile, mine }));
      return;
    }
    navigate(paths.jobs({ view: "work", dueWindow: tile, mine }));
  }

  return (
    <div className="space-y-8">
      <div>
        <div className="flex items-center justify-between mb-1 gap-4 flex-wrap">
          <h1 className="text-2xl">CSR/AM command centre</h1>
          <div className="flex items-center gap-3">
            {canToggleScope && (
              <button
                type="button"
                aria-pressed={showAll}
                onClick={() => setShowAll(!showAll)}
                className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${
                  showAll
                    ? "bg-primary text-white border-primary"
                    : "bg-surface border-border text-foreground hover:bg-surface-hover"
                }`}
              >
                Show all customers
              </button>
            )}
            <HomePersonaToggle current="csr" onSelect={switchPersona} />
          </div>
        </div>
        <p className="text-sm text-muted-foreground">
          Your jobs, worst first — what hasn&apos;t started moving, what&apos;s
          due, and what&apos;s late.
        </p>
      </div>

      {dashboard.assigned_customer_count === 0 && (
        <div
          role="status"
          className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900/50 dark:bg-amber-900/20 dark:text-amber-200"
        >
          <AlertTriangle className="size-4 mt-0.5 shrink-0" />
          <p>
            <span className="font-medium">
              No customers are currently assigned to you.
            </span>{" "}
            This board only shows jobs you&apos;ve booked in yourself —
            customer-scoped rails like upcoming meetings will stay empty until
            you have a portfolio. Ask an admin to assign you to customers in
            Tharstern
            {canToggleScope
              ? ', or use "Show all customers" above to see everything in the meantime.'
              : "."}
          </p>
        </div>
      )}

      <CommandCentreStatStrip
        counts={dashboard.summary_counts}
        onSelectTile={selectStatTile}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <CommandCentreReadinessStrip entries={dashboard.readiness_pipeline} />
          <WipFocusedBoard
            inFlightBoard={inFlightBoard}
            inFlightBoardTotal={inFlightBoardTotal}
            viewAllJobsHref={paths.jobs({ view: "work", mine: !showAll })}
            recentlyCompleted={dashboard.recent_completions}
            finishedAwaitingClose={dashboard.finished_awaiting_close}
            query={query}
            onQueryChange={setQuery}
            sort={sort}
            onSortChange={setSort}
            isSearchActive={isSearchActive}
            searchLoading={boardLoading}
          />
        </div>
        <div>
          <CommandCentreRail
            upcomingMeetings={dashboard.upcoming_meetings}
            tickets={dashboard.tickets}
            alerts={dashboard.alerts}
          />
        </div>
      </div>
    </div>
  );
}
